import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { ChevronDown, SearchIcon, XIcon } from 'lucide-react-native';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Keyboard, Platform, useWindowDimensions } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { groupOptions, type GroupedRow } from '@/components/ui/combobox/groupOptions';
import { Box } from '@/components/ui/box';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { Popover, PopoverBackdrop, PopoverBody, PopoverContent } from '@/components/ui/popover';
import { Pressable } from '@/components/ui/pressable';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { fuzzyMatchLabel } from '@/util/fuzzyMatchLabel';
import { highlightMatch } from '@/util/highlightMatch';

/** Left padding per depth, as literal strings so Tailwind sees every class. Depth 3 and up share one. */
const INDENT_CLASSES = ['pl-4', 'pl-8', 'pl-12', 'pl-16'];

/** InputField with web-only onKeyDown for keyboard nav. */
const InputFieldWeb = InputField as React.ComponentType<
  React.ComponentProps<typeof InputField> & {
    onKeyDown?: (e: { key: string; preventDefault: () => void }) => void;
  }
>;

/** One selectable option. */
export type ComboboxOption = {
  /** The option's id as a string. The empty string is reserved for "no selection". */
  value: string;
  label: string;
  /** A short form of the label, matched by search, such as an abbreviation. */
  shortLabel?: string | null;
  /** Other names for the option, matched by search. */
  synonyms?: string[];
  /** Value of the parent option. Only read in `grouped` mode. */
  parentValue?: string | null;
  /** Published post count, descendants included. Shown beside the label when set, unless `showCounts` is off. */
  count?: number;
};

/** Optional hooks so a caller can report picker activity without the picker knowing about analytics. */
export type ComboboxAnalytics = {
  /** The list was opened (web dropdown or native sheet). */
  onViewed?: () => void;
  /** First non-empty search term typed since the list was opened. */
  onSearched?: (searchTerm: string) => void;
  /** An item was picked. `value` is '' for the native "Select All" item. Not called by the web clear X. */
  onSelected?: (value: string) => void;
};

export type ComboboxProps = {
  /** Selected option's value, or '' for none. */
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  /**
   * Show the options as a tree: parent headers with their subtypes indented beneath, one step
   * per depth, alphabetical within each level. Parents and subtypes are both selectable, and
   * search keeps a matching subtype under its parent and a matching parent with its subtypes.
   * Off by default, which keeps the list flat in the order given.
   */
  grouped?: boolean;
  /** Drop options whose `count` is 0. Options without a count are kept. */
  hideEmpty?: boolean;
  /**
   * Show each option's `count` beside its label and in its accessible name. Off keeps the counts
   * out of the rows and the labels, while `hideEmpty` still reads them. On by default.
   */
  showCounts?: boolean;
  /** The options are still loading, so an empty list is not reported as having no options. */
  isLoading?: boolean;
  /** Leading magnifier on the web field. Off where the picker sits near a keyword search. */
  showSearchIcon?: boolean;
  /** Extra classes for the web trigger's field box, such as a height. */
  fieldClassName?: string;
  placeholder?: string;
  /** Accessible name of the field. */
  label?: string;
  searchPlaceholder?: string;
  /** Native sheet item that clears the selection. */
  clearOptionLabel?: string;
  /** Accessible name of the web clear button. */
  clearLabel?: string;
  /** Shown when a search matches no option and the options are not loading. */
  noOptionsLabel?: string;
  /** Accessible name of the native search box's clear button. */
  clearSearchLabel?: string;
  /** Accessible name of the web options list. */
  optionsLabel?: string;
  /** Test id of the web options list, for end-to-end suites that open it. */
  listboxTestId?: string;
  /** Overrides the label shown for the selected value, which is otherwise looked up in `options`. */
  selectedLabel?: string;
  analytics?: ComboboxAnalytics;
  className?: string;
  /** Text color for the field, as a resolved color string. */
  textColor?: string;
  /** Bottom safe-area inset for the native sheet. */
  bottomInset?: number;
};

/**
 * Searchable single-select dropdown. Web uses a searchable listbox in a popover (keyboard nav,
 * match highlighting, clear via the trigger X). Native uses a Select bottom sheet with search and
 * a "Select All" item that clears the selection.
 *
 * It knows nothing about what the options are. Every word it shows, the placeholder, the
 * accessible names and the empty message, comes in through props, so a screen names it for
 * its own subject and two filters beside each other can be this one component and cannot drift
 * apart. The cancer type and treatment type filters in the directory are both drawn with it.
 */
export function Combobox({
  value,
  onChange,
  options,
  grouped = false,
  hideEmpty = false,
  showCounts = true,
  isLoading = false,
  showSearchIcon = true,
  fieldClassName,
  placeholder = 'Select',
  label = 'Select',
  searchPlaceholder = 'Search',
  clearOptionLabel = 'Select All',
  clearLabel = 'Clear filter',
  noOptionsLabel = 'No options found',
  clearSearchLabel = 'Clear search',
  optionsLabel = 'Options',
  listboxTestId = 'combobox-listbox',
  selectedLabel,
  analytics,
  className,
  textColor,
  bottomInset = 0,
}: ComboboxProps) {
  const selectedValue = value;

  const bottom = bottomInset;

  const selectedOptionLabel =
    selectedLabel ?? options.find((option) => option.value === value)?.label ?? '';

  const analyticsRef = useRef(analytics);

  analyticsRef.current = analytics;

  const [searchTerm, setSearchTerm] = useState('');

  const [isOpen, setIsOpen] = useState(false);

  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const searchInputRef = useRef<React.ComponentRef<typeof InputField>>(null);

  const optionRefs = useRef<unknown[]>([]);

  const triggerBoxRef = useRef<unknown>(null);

  const popoverContentRef = useRef<unknown>(null);

  const hasTrackedSearchRef = useRef(false);

  const generatedId = useId();

  const inputId = `combobox-input-${generatedId}`;

  const listboxId = `combobox-listbox-${generatedId}`;

  const { height: windowHeight } = useWindowDimensions();

  const rows = useMemo(() => {
    const trimmedQuery = searchTerm.trim();

    const visibleOptions = hideEmpty ? options.filter((option) => option.count !== 0) : options;

    if (grouped) {
      return groupOptions(visibleOptions, trimmedQuery);
    }

    return (
      trimmedQuery
        ? visibleOptions.filter((option) =>
            fuzzyMatchLabel(
              option.label,
              option.shortLabel ?? '',
              option.synonyms ?? [],
              trimmedQuery
            )
          )
        : visibleOptions
    ).map((option) => ({ option, depth: 0, parentLabel: undefined, hasChildren: false }));
  }, [options, searchTerm, grouped, hideEmpty]);

  const filteredOptions = useMemo(() => rows.map((row) => row.option), [rows]);

  const nativeSheetHeight = useMemo(() => {
    const collapsedHeight = Math.round(windowHeight * 0.7);

    const expandedHeight = Math.round(windowHeight * 0.9);

    return isSearchFocused || keyboardHeight > 0 ? expandedHeight : collapsedHeight;
  }, [isSearchFocused, keyboardHeight, windowHeight]);

  const nativeListBottomInset = useMemo(
    () => keyboardHeight + bottom + 12,
    [keyboardHeight, bottom]
  );

  const listOptionCount = filteredOptions.length;

  const openDropdown = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        hasTrackedSearchRef.current = false;

        setSearchTerm('');

        analyticsRef.current?.onViewed?.();
      }

      return true;
    });
  }, []);

  const closeDropdown = useCallback(() => {
    hasTrackedSearchRef.current = false;

    setSearchTerm('');

    setIsSearchFocused(false);

    setKeyboardHeight(0);

    setIsOpen(false);
  }, []);

  const handleSearchTermChange = useCallback((nextValue: string) => {
    const trimmedValue = nextValue.trim();

    if (trimmedValue && !hasTrackedSearchRef.current) {
      hasTrackedSearchRef.current = true;

      analyticsRef.current?.onSearched?.(trimmedValue);
    }

    setSearchTerm(nextValue);
  }, []);

  const handleSelect = useCallback(
    (nextFocusId: string, _label: string) => {
      analyticsRef.current?.onSelected?.(nextFocusId);

      onChange(nextFocusId);

      setSearchTerm('');

      setIsOpen(false);
    },
    [onChange]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const idx = selectedValue ? filteredOptions.findIndex((o) => o.value === selectedValue) : 0;

    setHighlightedIndex(idx >= 0 ? idx : 0);
  }, [isOpen, selectedValue, filteredOptions]);

  useEffect(() => {
    setHighlightedIndex((i) => Math.min(i, Math.max(0, listOptionCount - 1)));
  }, [listOptionCount]);

  useEffect(() => {
    if (!isWeb || !isOpen) {
      return;
    }

    const el = triggerBoxRef.current;

    if (!el || typeof (el as HTMLElement).getBoundingClientRect !== 'function') {
      return;
    }

    const rafId = requestAnimationFrame(() => {
      const rect = (el as HTMLElement).getBoundingClientRect();

      if (rect.width > 0) {
        setTriggerWidth(rect.width);
      }
    });

    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isWeb || !isOpen) {
      return;
    }

    const el = optionRefs.current[highlightedIndex];

    if (el && typeof (el as unknown as HTMLElement).scrollIntoView === 'function') {
      (el as unknown as HTMLElement).scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    if (isWeb) {
      return;
    }

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';

    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSubscription = Keyboard.addListener(showEvent, (event) => {
      setKeyboardHeight(event.endCoordinates.height);
    });

    const hideSubscription = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSubscription.remove();

      hideSubscription.remove();
    };
  }, []);

  const selectHighlighted = useCallback(() => {
    const option = filteredOptions[highlightedIndex];

    if (option) {
      handleSelect(option.value, option.label);
    }
  }, [highlightedIndex, filteredOptions, handleSelect]);

  const handleKeyDown = useCallback(
    (e: { key?: string; nativeEvent?: { key?: string }; preventDefault?: () => void }) => {
      const key = e?.nativeEvent?.key ?? e?.key;

      if (!key) {
        return;
      }

      if (key === 'Escape') {
        closeDropdown();

        e.preventDefault?.();

        return;
      }

      if (!isOpen) {
        if (key === 'ArrowDown' || key === 'Enter') {
          openDropdown();

          e.preventDefault?.();
        }

        return;
      }

      if (key === 'Enter') {
        if (listOptionCount > 0) {
          selectHighlighted();

          e.preventDefault?.();
        }

        return;
      }

      if (key === 'ArrowDown') {
        setHighlightedIndex((i) => Math.min(i + 1, listOptionCount - 1));

        e.preventDefault?.();

        return;
      }

      if (key === 'ArrowUp') {
        setHighlightedIndex((i) => Math.max(i - 1, 0));

        e.preventDefault?.();
      }
    },
    [isOpen, listOptionCount, selectHighlighted, closeDropdown]
  );

  useEffect(() => {
    if (!isWeb || !isOpen) {
      return;
    }

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;

      if (!target) {
        return;
      }

      const triggerEl = triggerBoxRef.current as HTMLElement | null;

      const contentEl = popoverContentRef.current as HTMLElement | null;

      const inside = triggerEl?.contains(target) || contentEl?.contains(target);

      if (!inside) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);

    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [isOpen, closeDropdown]);

  function renderWebTriggerSlotIcon() {
    if (searchTerm || selectedValue) {
      return (
        <Pressable
          role="button"
          aria-label={clearLabel}
          onPress={() => {
            onChange('');

            closeDropdown();
          }}>
          <InputIcon as={XIcon} size="lg" className="text-typography-500" />
        </Pressable>
      );
    }

    return <InputIcon as={ChevronDown} size="lg" className="text-typography-500" />;
  }

  const optionLabelClassName = 'text-sm text-typography-900';

  function renderOptionLabel(option: { label: string }, labelClassName = optionLabelClassName) {
    if (searchTerm.trim()) {
      return highlightMatch(option.label, searchTerm, labelClassName);
    }

    return option.label;
  }

  function formatCount(count: number) {
    return `${count} ${count === 1 ? 'post' : 'posts'}`;
  }

  /** Screen reader name: the group and subtype relationship is spoken, not only indented. */
  function describeRow(row: GroupedRow<ComboboxOption>) {
    const parts = [row.option.label];

    if (row.hasChildren) {
      parts.push('group');
    }

    if (row.parentLabel) {
      parts.push(`in ${row.parentLabel}`);
    }

    if (showCounts && row.option.count != null) {
      parts.push(formatCount(row.option.count));
    }

    return parts.join(', ');
  }

  function renderNoOptionsMessage() {
    if (filteredOptions.length === 0 && !isLoading) {
      return <Text className="px-4 py-3 text-sm text-typography-500">{noOptionsLabel}</Text>;
    }

    return null;
  }

  function renderNativeSearchClearButton() {
    if (!searchTerm) {
      return null;
    }

    return (
      <InputSlot
        className="flex min-h-[44px] min-w-[48px] items-center justify-center pr-2"
        role="button"
        aria-label={clearSearchLabel}
        onPress={() => setSearchTerm('')}>
        <InputIcon as={XIcon} size="lg" className="text-typography-500" />
      </InputSlot>
    );
  }

  if (isWeb) {
    return (
      <Popover
        className={twJoin(className, 'web:pointer-events-auto')}
        initialFocusRef={searchInputRef}
        placement="bottom"
        isOpen={isOpen}
        onOpen={openDropdown}
        onClose={closeDropdown}
        // Trigger rendering is self-contained here so a variant (e.g. a filter chip) can replace it.
        trigger={(triggerProps: Record<string, unknown>) => {
          const { ref: triggerRef, ...rest } = triggerProps;

          return (
            <Box
              ref={(el) => {
                if (
                  typeof triggerRef === 'object' &&
                  triggerRef != null &&
                  'current' in triggerRef
                ) {
                  (triggerRef as React.MutableRefObject<unknown>).current = el;
                }

                triggerBoxRef.current = el;
              }}
              className={className ?? 'w-full'}
              onLayout={(e) => setTriggerWidth(e.nativeEvent.layout.width)}
              collapsable={isWeb ? undefined : false}>
              <Pressable {...rest} className="w-full">
                <Input className={twJoin('gap-1 overflow-visible bg-background-0', fieldClassName)}>
                  {showSearchIcon ? (
                    <InputSlot className="h-full shrink-0 cursor-default pl-3 pr-0">
                      <InputIcon as={SearchIcon} size="lg" className="text-typography-500" />
                    </InputSlot>
                  ) : null}
                  <InputFieldWeb
                    ref={searchInputRef}
                    nativeID={inputId}
                    className={twJoin(
                      'min-w-0 flex-1 pr-4 placeholder:text-typography-600',
                      showSearchIcon ? 'pl-2' : 'pl-3'
                    )}
                    style={textColor ? { color: textColor } : undefined}
                    placeholder={placeholder}
                    accessibilityLabel={label}
                    aria-label={label}
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-activedescendant={
                      isOpen && listOptionCount > 0
                        ? `combobox-option-${highlightedIndex}`
                        : undefined
                    }
                    aria-autocomplete="list"
                    role="combobox"
                    value={isOpen ? searchTerm : selectedOptionLabel || ''}
                    onFocus={openDropdown}
                    onChangeText={(nextValue) => {
                      if (!isOpen) {
                        openDropdown();
                      }

                      handleSearchTermChange(nextValue);
                    }}
                    onKeyDown={(e) =>
                      handleKeyDown({
                        key: e.key,
                        preventDefault: () => e.preventDefault(),
                      })
                    }
                  />
                  <InputSlot className="flex min-h-[44px] min-w-10 shrink-0 items-center justify-center pr-3">
                    {renderWebTriggerSlotIcon()}
                  </InputSlot>
                </Input>
              </Pressable>
            </Box>
          );
        }}>
        <PopoverBackdrop />
        <PopoverContent
          size="full"
          className="max-h-96 shrink-0 p-0"
          style={
            triggerWidth != null
              ? {
                  width: triggerWidth,
                  minWidth: triggerWidth,
                  maxWidth: triggerWidth,
                  flexShrink: 0,
                }
              : undefined
          }>
          <Box
            ref={(el) => {
              popoverContentRef.current = el;
            }}
            className="max-h-96 w-full"
            style={
              triggerWidth != null
                ? {
                    width: triggerWidth,
                    minWidth: triggerWidth,
                    maxWidth: triggerWidth,
                    flexShrink: 0,
                  }
                : undefined
            }>
            <PopoverBody className="max-h-96 p-0">
              <Box
                {...(isWeb ? { id: listboxId } : { nativeID: listboxId })}
                testID={listboxTestId}
                aria-label={optionsLabel}
                className="flex-1 bg-background-0">
                {rows.map((row, idx) => {
                  const { option } = row;

                  const labelClassName = twJoin(
                    optionLabelClassName,
                    grouped && row.hasChildren && 'font-semibold'
                  );

                  return (
                    <Pressable
                      key={option.value}
                      ref={(el) => {
                        optionRefs.current[idx] = el;
                      }}
                      {...(isWeb
                        ? { id: `combobox-option-${idx}` }
                        : { nativeID: `combobox-option-${idx}` })}
                      role="option"
                      aria-selected={selectedValue === option.value}
                      aria-label={grouped ? describeRow(row) : undefined}
                      className={twJoin(
                        'bg-background-0 py-4 hover:bg-background-50 active:bg-background-100',
                        grouped ? ['pr-4', INDENT_CLASSES[Math.min(row.depth, 3)]] : 'px-4',
                        selectedValue === option.value ? 'bg-background-100' : '',
                        highlightedIndex === idx ? 'bg-background-100' : ''
                      )}
                      onPress={() => handleSelect(option.value, option.label)}>
                      {showCounts && option.count != null ? (
                        <Box className="flex-row items-center justify-between gap-3">
                          <Text className={twJoin(labelClassName, 'flex-1')}>
                            {renderOptionLabel(option, labelClassName)}
                          </Text>
                          <Text className="text-sm text-typography-700">{option.count}</Text>
                        </Box>
                      ) : (
                        <Text className={labelClassName}>
                          {renderOptionLabel(option, labelClassName)}
                        </Text>
                      )}
                    </Pressable>
                  );
                })}
                {renderNoOptionsMessage()}
              </Box>
            </PopoverBody>
          </Box>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Select
      className={className}
      onOpen={() => {
        setSearchTerm('');

        analyticsRef.current?.onViewed?.();
      }}
      onValueChange={(nextFocusId) => {
        analyticsRef.current?.onSelected?.(nextFocusId);

        onChange(nextFocusId);

        setSearchTerm('');
      }}
      selectedValue={selectedValue || ''}>
      <SelectTrigger
        size="md"
        variant="outline"
        className="flex w-full items-center justify-between gap-2 bg-background-0 px-4 py-2">
        <SelectInput
          className="min-w-0 flex-1 px-4 placeholder:text-typography-600"
          style={textColor ? { color: textColor } : undefined}
          placeholder={placeholder}
          value={selectedOptionLabel}
        />
        <SelectIcon as={ChevronDown} size="lg" className="shrink-0 text-typography-600" />
      </SelectTrigger>
      <SelectPortal>
        <SelectBackdrop />
        <SelectContent
          className="flex w-full shrink-0 flex-col bg-background-0"
          style={{
            minHeight: nativeSheetHeight,
            maxHeight: nativeSheetHeight,
            paddingBottom: bottom,
          }}>
          <SelectDragIndicatorWrapper>
            <SelectDragIndicator />
          </SelectDragIndicatorWrapper>

          <Input className="mx-2 mb-4 mt-3 h-10 shrink-0 gap-1 bg-background-0">
            <InputSlot className="h-full shrink-0 cursor-default pl-3 pr-0">
              <InputIcon as={SearchIcon} size="lg" className="text-typography-500" />
            </InputSlot>
            <InputField
              className="pl-2 pr-4 placeholder:text-typography-600"
              style={textColor ? { color: textColor } : undefined}
              placeholder={searchPlaceholder}
              value={searchTerm}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              onChangeText={handleSearchTermChange}
            />
            {renderNativeSearchClearButton()}
          </Input>

          <SelectScrollView
            className="min-h-[200px] flex-1"
            contentContainerStyle={{ paddingBottom: nativeListBottomInset }}
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            keyboardShouldPersistTaps="handled"
            scrollIndicatorInsets={{ bottom: nativeListBottomInset }}>
            <SelectItem
              key="Select All"
              label={clearOptionLabel}
              value=""
              className={twJoin('py-4 text-typography-900', !selectedValue && 'bg-background-100')}
            />
            {rows.map((row) => (
              <SelectItem
                key={row.option.value}
                label={
                  showCounts && row.option.count != null
                    ? `${row.option.label} (${row.option.count})`
                    : row.option.label
                }
                value={row.option.value}
                accessibilityLabel={grouped ? describeRow(row) : undefined}
                className={twJoin(
                  'py-4 text-typography-900',
                  grouped && 'pr-4',
                  grouped && INDENT_CLASSES[Math.min(row.depth, 3)],
                  grouped && row.hasChildren && 'font-semibold',
                  selectedValue === row.option.value && 'bg-background-100'
                )}
              />
            ))}
            {renderNoOptionsMessage()}
          </SelectScrollView>
        </SelectContent>
      </SelectPortal>
    </Select>
  );
}
